import { useQuery } from "@tanstack/react-query";
import * as Styled from "./styled";
import useApi from "@/contexts/ApiContext";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import type {
  RoadmapItemLongTerm,
  RoadmapItemShortTerm,
} from "@/services/api/types";
import ContactCard from "@/components/shared-ui/ContactCard";
import Page from "@/components/layout/Page";
import RoadmapIntroduction from "./RoadmapIntroduction";
import { useState } from "react";
import DetailsModal from "./DetailsModal";
import TabbedDisplay from "@/components/shared-ui/TabbedDisplay";
import Roadmap from "./Roadmap";

const LONG_TERM_TAB_ID = "longer_term";

const ProductRoadmap = () => {
  const api = useApi();
  const [selectedItem, setSelectedItem] = useState<
    RoadmapItemShortTerm | RoadmapItemLongTerm | undefined
  >();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: roadmapData } = useQuery({
    queryKey: ["get-roadmap"],
    queryFn: () => api.roadmap.getAll(),
  });

  if (!roadmapData) {
    return <LoadingScreen withoutPage />;
  }

  const handleSelect = (item: RoadmapItemShortTerm | RoadmapItemLongTerm) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedItem(undefined);
  };

  const handleTabChange = (tabId: string) => {
    if (tabId !== LONG_TERM_TAB_ID) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "roadmap_view",
      roadmap_type: "longer_term",
    });
  };

  const tabs = [
    {
      id: "now",
      label: (
        <>
          Now <Styled.Date>({roadmapData.date})</Styled.Date>
        </>
      ),
      content: (
        <>
          <Styled.Summary>
            What we’re currently working on, grouped by platform area. Click
            into each item for more detail – including, for larger initiatives,
            a document outlining what’s in and out of scope. Items are updated
            monthly, and the roadmap is refreshed each quarter to reflect
            current priorities.
          </Styled.Summary>
          <Roadmap data={roadmapData.st} handleSelect={handleSelect} />
        </>
      ),
    },
    {
      id: LONG_TERM_TAB_ID,
      label: "Areas we’re exploring",
      content: (
        <>
          <Styled.Summary>
            These are some of the areas we’re currently exploring and how we’re
            thinking about Seccl’s evolution. They’re not delivery commitments,
            but instead an indication of what we’re investigating and
            considering. Our priorities and focus will continue to be led by
            customer feedback, market developments and strategic opportunities,
            so we’ll review and refresh this over time.
          </Styled.Summary>
          <Styled.Summary>
            Have thoughts on what we should prioritise or see something that
            interests you? Get in touch with your account manager to discuss -
            we’d love to hear from you.
          </Styled.Summary>
          <h3>Major upgrades and projects</h3>
          <Roadmap data={roadmapData.ltm} handleSelect={handleSelect} />
          <h3>Ongoing improvements</h3>
          <Roadmap data={roadmapData.lto} handleSelect={handleSelect} />
        </>
      ),
    },
  ];

  return (
    <Page whiteBackground title="Product roadmap">
      <Styled.ProductRoadmap>
        <RoadmapIntroduction />
        <TabbedDisplay tabs={tabs} onTabChange={handleTabChange} />
      </Styled.ProductRoadmap>
      {selectedItem && (
        <DetailsModal
          item={selectedItem}
          modalActive={modalOpen}
          toggleModal={() => handleClose()}
        />
      )}
      <ContactCard />
    </Page>
  );
};

export default ProductRoadmap;
